/**
 * Nest-page parsing.
 *
 * A nest page in a Kip coop is YAML frontmatter (`type`, `created`, `updated`,
 * `tags`) followed by a markdown body with no top-level `# Title` — the title
 * is the filename slug. This reads that format for interoperability.
 */
import { DIR_TYPES, type NestPage, type NestPageFrontmatter, type PageType } from './coop-types.js';
import { extractWikilinkSlugs } from './slug.js';

// --- frontmatter split + a YAML subset -------------------------------------
//
// Nest frontmatter is a handful of scalars plus a `tags` list — inline `[a, b]`
// or a block `- a`. A ~40-line parser covers it and keeps `js-yaml` (100 KB+)
// out of a mobile bundle. Anything it doesn't recognise is simply skipped.

const FRONTMATTER_RE = /^\uFEFF?---\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/;

function unquote(s: string): string {
  return s.replace(/^["']|["']$/g, '');
}

function parseFrontmatterYaml(src: string): NestPageFrontmatter {
  const out: Record<string, unknown> = {};
  let listKey: string | null = null;
  let list: string[] | null = null;

  const flush = () => {
    if (listKey && list) out[listKey] = list;
    listKey = null;
    list = null;
  };

  for (const line of src.split(/\r?\n/)) {
    if (!line.trim() || line.trim().startsWith('#')) continue;

    const item = /^\s*-\s+(.*)$/.exec(line);
    if (item && listKey) {
      (list ??= []).push(unquote(item[1]!.trim()));
      continue;
    }

    const kv = /^([A-Za-z0-9_-]+):[ \t]*(.*)$/.exec(line);
    if (!kv) continue;
    flush();
    const key = kv[1]!;
    const value = kv[2]!.trim();

    if (value === '') {
      listKey = key; // a block list may follow
    } else if (value.startsWith('[') && value.endsWith(']')) {
      out[key] = value
        .slice(1, -1)
        .split(',')
        .map((s) => unquote(s.trim()))
        .filter(Boolean);
    } else {
      out[key] = unquote(value);
    }
  }
  flush();
  return out as NestPageFrontmatter;
}

/** `--- yaml --- body` → `{ data, body }`. No fence → all body. */
function splitFrontmatter(raw: string): { data: NestPageFrontmatter; body: string } {
  const m = FRONTMATTER_RE.exec(raw);
  if (!m) return { data: {}, body: raw.trim() };
  return { data: parseFrontmatterYaml(m[1]!), body: raw.slice(m[0].length).trim() };
}

/** `nest/concepts/sleep-hygiene.md` -> `sleep-hygiene`. */
export function slugFromPath(path: string): string {
  const base = path.split('/').pop() ?? path;
  return base.replace(/\.md$/i, '');
}

/** `nest/concepts/x.md` -> `concept`. Falls back to frontmatter, then `concept`. */
export function typeFromPath(path: string, fmType?: PageType): PageType {
  const parts = path.split('/');
  const dir = parts[parts.length - 2];
  if (dir && DIR_TYPES[dir]) return DIR_TYPES[dir]!;
  return fmType ?? 'concept';
}

/** First line of real prose, markdown stripped, for the browsing summary. */
export function deriveSummary(body: string): string {
  for (const rawLine of body.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    if (line.startsWith('#')) continue; // heading
    if (line.startsWith('---')) continue; // update separator
    if (/^_Update \d{4}-\d{2}-\d{2}:_$/.test(line)) continue;
    return line
      .replace(/\[\[([^\]]+)\]\]/g, '$1') // wikilinks -> text
      .replace(/[*_`>#-]/g, '') // light markdown strip
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 280);
  }
  return '';
}

function normalizeTags(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map((t) => String(t)).filter(Boolean);
  if (typeof raw === 'string' && raw.trim()) return [raw.trim()];
  return [];
}

/**
 * YAML parses an unquoted `2026-08-30` into a `Date`. Normalize back to the
 * `YYYY-MM-DD` string the nest format uses, tolerating a plain string too.
 */
function isoDate(raw: unknown): string {
  if (!raw) return '';
  if (raw instanceof Date) return raw.toISOString().slice(0, 10);
  const s = String(raw).trim();
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  return m ? m[1]! : s;
}

/**
 * Parse one nest page's raw markdown into the cached shape.
 * `path` is the coop-relative, forward-slashed path; `rev` the Dropbox rev.
 */
export function parseNestPage(path: string, raw: string, rev?: string): NestPage {
  const { data, body } = splitFrontmatter(raw);
  const slug = slugFromPath(path);
  const type = typeFromPath(path, data.type);
  return {
    slug,
    path,
    type,
    tags: normalizeTags(data.tags),
    created: isoDate(data.created),
    updated: isoDate(data.updated),
    body,
    summary: deriveSummary(body),
    links: [...new Set(extractWikilinkSlugs(body))],
    rev,
  };
}
